import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Search, ArrowUpDown, ArrowUp, ArrowDown, Eye, Video,
  FileText, ChevronLeft, ChevronRight, CheckCircle2, XCircle,
  Clock, Send, Target, Zap, TrendingUp, AlertTriangle,
  Shield, MapPin, Building2, Calendar, Star, Link2, ExternalLink,
  Download, Tag, Loader2, X, Edit2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { generateInterviewLink, assignCandidateTags, getTags, updateCandidate } from '../services/api';
import { ScoutBadge } from './scout/ScoutBadge';
import { useAuth } from '../context/AuthContext';

const FIT_COLORS = {
  'Strong Fit': 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  'Good Fit': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  'Risky Fit': 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  'Moderate Fit': 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  'Weak Fit': 'bg-red-500/10 text-red-600 border-red-500/20',
};

const INTERVIEW_STATUS_CONFIG = {
  none: { label: 'No Link', color: 'text-muted-foreground bg-muted/50', icon: Clock },
  link_sent: { label: 'Link Sent', color: 'text-blue-600 bg-blue-500/10', icon: Send },
  in_progress: { label: 'In Progress', color: 'text-amber-600 bg-amber-500/10', icon: Clock },
  completed: { label: 'Completed', color: 'text-emerald-600 bg-emerald-500/10', icon: CheckCircle2 },
};

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || (typeof window !== 'undefined' ? window.location.origin : '');

// ===== Fitment Score Popup =====
const FitmentPopup = ({ candidate, open, onClose }) => {
  const an = candidate?.analysis_summary;
  if (!an) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]" data-testid="fitment-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Target className="h-5 w-5 text-primary" />
            Fitment Analysis — {candidate.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className={`text-3xl font-bold ${candidate.final_score >= 80 ? 'text-emerald-500' : candidate.final_score >= 60 ? 'text-blue-500' : candidate.final_score >= 40 ? 'text-amber-500' : 'text-red-500'}`}
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {candidate.final_score ? Math.round(candidate.final_score) : '—'}
            </div>
            <div>
              <Badge className={FIT_COLORS[candidate.fit_label] || 'bg-muted'}>{candidate.fit_label || 'Pending'}</Badge>
              {an.recommendation && <p className="text-xs text-muted-foreground mt-1">{an.recommendation}</p>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            {an.skill_fit_score != null && (
              <div className="p-2 rounded-md border bg-muted/20">
                <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{typeof an.skill_fit_score === 'object' ? an.skill_fit_score.score ?? '—' : an.skill_fit_score}</p>
                <p className="text-[10px] text-muted-foreground">Skill Fit</p>
              </div>
            )}
            {an.growth_potential != null && (
              <div className="p-2 rounded-md border bg-muted/20">
                <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{typeof an.growth_potential === 'object' ? an.growth_potential.score ?? '—' : an.growth_potential}</p>
                <p className="text-[10px] text-muted-foreground">Growth</p>
              </div>
            )}
            {an.stability_risk != null && (
              <div className="p-2 rounded-md border bg-muted/20">
                <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{typeof an.stability_risk === 'object' ? an.stability_risk.level ?? '—' : an.stability_risk}</p>
                <p className="text-[10px] text-muted-foreground">Stability</p>
              </div>
            )}
          </div>

          {an.green_flags?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-emerald-600 mb-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Green Flags</p>
              <div className="space-y-1">
                {an.green_flags.map((f, i) => <p key={i} className="text-xs text-muted-foreground pl-4">+ {f}</p>)}
              </div>
            </div>
          )}
          {an.red_flags?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-500 mb-1 flex items-center gap-1"><XCircle className="h-3 w-3" /> Red Flags</p>
              <div className="space-y-1">
                {an.red_flags.map((f, i) => <p key={i} className="text-xs text-muted-foreground pl-4">- {f}</p>)}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== Candidate Insight Popup =====
const CandidateInsightPopup = ({ candidate, open, onClose }) => {
  if (!candidate) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px]" data-testid="candidate-insight-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Star className="h-5 w-5 text-primary" />
            {candidate.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {candidate.email && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="font-medium text-foreground">Email:</span> {candidate.email}
              </div>
            )}
            {candidate.phone && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="font-medium text-foreground">Phone:</span> {candidate.phone}
              </div>
            )}
            {candidate.location && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0" /> {candidate.location}
              </div>
            )}
            {candidate.current_company && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building2 className="h-3.5 w-3.5 shrink-0" /> {candidate.current_company}
              </div>
            )}
            {candidate.experience_years != null && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 shrink-0" /> {candidate.experience_years} years exp
              </div>
            )}
          </div>

          {candidate.skills?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Skills</p>
              <div className="flex flex-wrap gap-1">
                {candidate.skills.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px]">{s}</Badge>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border bg-muted/20">
              <p className="text-xs text-muted-foreground">Resume Score</p>
              <p className={`text-xl font-bold mt-1 ${candidate.final_score >= 80 ? 'text-emerald-500' : candidate.final_score >= 60 ? 'text-blue-500' : 'text-amber-500'}`}
                style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {candidate.final_score ? Math.round(candidate.final_score) : '—'}
              </p>
            </div>
            <div className="p-3 rounded-lg border bg-muted/20">
              <p className="text-xs text-muted-foreground">Best Interview Score</p>
              <p className={`text-xl font-bold mt-1 ${candidate.interview_score >= 70 ? 'text-emerald-500' : candidate.interview_score >= 50 ? 'text-amber-500' : candidate.interview_score ? 'text-red-500' : 'text-muted-foreground'}`}
                style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {candidate.interview_score ? Math.round(candidate.interview_score) : '—'}
              </p>
            </div>
          </div>

          {/* All Attempts */}
          {candidate.attempts?.filter(a => a.status === 'completed').length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Interview Attempts ({candidate.attempts.filter(a => a.status === 'completed').length} completed of {candidate.interview_count || candidate.attempts.length})
              </p>
              <div className="space-y-1.5">
                {candidate.attempts.filter(a => a.status === 'completed').map((att, i) => {
                  const sc = att.score ? Math.round(att.score) : null;
                  const isBest = sc === Math.round(candidate.interview_score || 0);
                  return (
                    <div key={i} className={`flex items-center justify-between p-2 rounded-md border text-sm ${isBest ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-muted/10'}`}
                      data-testid={`insight-attempt-${i}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant={isBest ? 'default' : 'secondary'} className="text-[10px] h-5">
                          #{att.attempt || i + 1}
                        </Badge>
                        <span className={`font-bold ${sc >= 70 ? 'text-emerald-500' : sc >= 50 ? 'text-amber-500' : 'text-red-500'}`}
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {sc ?? '—'}
                        </span>
                        {isBest && <Badge className="bg-emerald-500 text-white text-[9px] h-4">Best</Badge>}
                        {att.completed_at && (
                          <span className="text-[10px] text-muted-foreground">
                            {(() => { try { return format(parseISO(att.completed_at), 'MMM d, HH:mm'); } catch { return ''; } })()}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-6 text-xs gap-1 text-primary"
                          onClick={() => window.open(`${PUBLIC_URL}/interview/${att.token}?recruiter=1`, '_blank')}
                          data-testid={`view-attempt-${i}`}>
                          <Eye className="h-3 w-3" /> View
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/interview/${att.token}/report-pdf`, '_blank')}
                          data-testid={`download-attempt-${i}`}
                          title="Download PDF">
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pending/In-progress attempts */}
          {candidate.attempts?.filter(a => a.status !== 'completed').length > 0 && (
            <div className="text-xs text-muted-foreground">
              + {candidate.attempts.filter(a => a.status !== 'completed').length} pending/in-progress attempt(s)
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== Column sort header =====
const SortHeader = ({ label, field, sortField, sortDir, onSort, className = '' }) => {
  const active = sortField === field;
  return (
    <button
      className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors ${className}`}
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      {label}
      {active ? (
        sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-40" />
      )}
    </button>
  );
};

// ===== MAIN TABLE COMPONENT =====
export const SmartCandidateTable = ({
  candidates,
  jobs = [],
  loading = false,
  showJobColumn = true,
  showBulkActions = false,
  jobId = null,
  onViewResume,
  onOpenInterview,
  onBulkLinksGenerated,
  pageSize = 25,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === 'viewer';
  const [search, setSearch] = useState('');
  const [jobFilter, setJobFilter] = useState('all');
  const [fitFilter, setFitFilter] = useState('all');
  const [interviewFilter, setInterviewFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);

  // Popups
  const [fitmentCandidate, setFitmentCandidate] = useState(null);
  const [insightCandidate, setInsightCandidate] = useState(null);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkTagging, setBulkTagging] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState('');

  const toggleSelect = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((currentFiltered, currentPage, currentPageSize) => {
    setSelected(prev => {
      const pageIds = currentFiltered.slice(currentPage * currentPageSize, (currentPage + 1) * currentPageSize).map(c => c.id);
      if (pageIds.every(id => prev.has(id))) {
        const next = new Set(prev);
        pageIds.forEach(id => next.delete(id));
        return next;
      }
      return new Set([...prev, ...pageIds]);
    });
  }, []);

  const clearSelection = () => setSelected(new Set());

  // Edit Candidate
  const [editCandidate, setEditCandidate] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', location: '' });
  const [editSaving, setEditSaving] = useState(false);

  const handleOpenEdit = () => {
    const sel = candidates.find(c => selected.has(c.id));
    if (!sel) return;
    setEditCandidate(sel);
    setEditForm({ name: sel.name || '', email: sel.email || '', phone: sel.phone || '', location: sel.location || '' });
  };

  const handleSaveEdit = async () => {
    if (!editCandidate) return;
    setEditSaving(true);
    try {
      await updateCandidate(editCandidate.job_id, editCandidate.id, editForm);
      toast.success('Candidate updated');
      setEditCandidate(null);
      clearSelection();
      onBulkLinksGenerated?.(); // refresh list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setEditSaving(false); }
  };

  // Bulk: Send Interview Links
  const handleBulkSendLinks = async () => {
    const toSend = candidates.filter(c => selected.has(c.id) && c.interview_status === 'none');
    if (toSend.length === 0) {
      toast.error('All selected candidates already have interview links');
      return;
    }
    const targetJobId = jobId || toSend[0].job_id;
    setBulkSending(true);
    let success = 0, failed = 0, blocked = 0;
    for (const c of toSend) {
      try {
        await generateInterviewLink(c.job_id, c.id);
        success++;
      } catch (e) { if (e?.response?.status === 409) blocked++; else failed++; }
    }
    setBulkSending(false);
    toast.success(`Generated ${success} interview link${success !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''}${blocked > 0 ? `, ${blocked} blocked by Scout (High Risk)` : ''}`);
    clearSelection();
    onBulkLinksGenerated?.();
  };

  // Bulk: Export CSV
  const handleBulkExportCSV = () => {
    const rows = candidates.filter(c => selected.has(c.id));
    if (rows.length === 0) return;
    const headers = ['Name', 'Email', 'Phone', 'Job', 'Score', 'Fit', 'Interview Status', 'Interview Score', 'Location', 'Company', 'Experience', 'Added'];
    const csvRows = [headers.join(',')];
    rows.forEach(c => {
      csvRows.push([
        `"${(c.name || '').replace(/"/g, '""')}"`,
        `"${c.email || ''}"`,
        `"${c.phone || ''}"`,
        `"${(c.job_title || '').replace(/"/g, '""')}"`,
        c.final_score ? Math.round(c.final_score) : '',
        `"${c.fit_label || ''}"`,
        c.interview_status,
        c.interview_score ? Math.round(c.interview_score) : '',
        `"${(c.location || '').replace(/"/g, '""')}"`,
        `"${(c.current_company || '').replace(/"/g, '""')}"`,
        c.experience_years ?? '',
        c.created_at ? c.created_at.substring(0, 10) : '',
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `candidates_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} candidates to CSV`);
  };

  // Bulk: Tag
  const handleOpenBulkTag = async () => {
    try {
      const data = await getTags();
      setAvailableTags(data.tags || data || []);
    } catch { setAvailableTags([]); }
    setShowTagDialog(true);
  };

  const handleBulkTag = async () => {
    if (!selectedTagId) { toast.error('Please select a tag'); return; }
    const rows = candidates.filter(c => selected.has(c.id));
    setBulkTagging(true);
    let success = 0;
    for (const c of rows) {
      try {
        const existing = c.tags || [];
        const tagIds = [...new Set([...existing.map(t => typeof t === 'string' ? t : t.id), selectedTagId])];
        await assignCandidateTags(c.job_id, c.id, tagIds);
        success++;
      } catch { /* tag assignment failed for one candidate */ }
    }
    setBulkTagging(false);
    setShowTagDialog(false);
    setSelectedTagId('');
    toast.success(`Tagged ${success} candidate${success > 1 ? 's' : ''}`);
    clearSelection();
    onBulkLinksGenerated?.();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'created_at' ? 'desc' : 'desc');
    }
    setPage(0);
  };

  const fitLabels = useMemo(() => [...new Set(candidates.map(c => c.fit_label).filter(Boolean))], [candidates]);

  const filtered = useMemo(() => {
    let list = candidates.filter(c => {
      const q = search.toLowerCase();
      const matchSearch = !q || (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
      const matchJob = jobFilter === 'all' || c.job_id === jobFilter;
      const matchFit = fitFilter === 'all' || c.fit_label === fitFilter;
      const matchInterview = interviewFilter === 'all' || c.interview_status === interviewFilter;
      return matchSearch && matchJob && matchFit && matchInterview;
    });

    list.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'name': va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); break;
        case 'final_score': va = a.final_score || 0; vb = b.final_score || 0; break;
        case 'interview_score': va = a.interview_score || 0; vb = b.interview_score || 0; break;
        case 'created_at': va = a.created_at || ''; vb = b.created_at || ''; break;
        case 'job_title': va = (a.job_title || '').toLowerCase(); vb = (b.job_title || '').toLowerCase(); break;
        case 'experience_years': va = a.experience_years || 0; vb = b.experience_years || 0; break;
        default: va = a.created_at || ''; vb = b.created_at || '';
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [candidates, search, jobFilter, fitFilter, interviewFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr.substring(0, 10); }
  };

  return (
    <div className="space-y-4" data-testid="smart-candidate-table">
      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search name or email..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="pl-9 h-9" data-testid="table-search" />
        </div>
        {showJobColumn && jobs.length > 1 && (
          <Select value={jobFilter} onValueChange={v => { setJobFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[180px] h-9" data-testid="table-job-filter"><SelectValue placeholder="All Jobs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={fitFilter} onValueChange={v => { setFitFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[140px] h-9" data-testid="table-fit-filter"><SelectValue placeholder="All Fits" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Fits</SelectItem>
            {fitLabels.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={interviewFilter} onValueChange={v => { setInterviewFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[150px] h-9" data-testid="table-interview-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Interview</SelectItem>
            <SelectItem value="none">No Link</SelectItem>
            <SelectItem value="link_sent">Link Sent</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-xs px-2.5 py-1 h-9 flex items-center">
          {filtered.length} of {candidates.length}
        </Badge>
      </div>

      {/* Bulk Action Bar */}
      {showBulkActions && selected.size > 0 && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg border bg-primary/5 border-primary/20" data-testid="bulk-action-bar">
          <Badge className="bg-primary text-primary-foreground text-xs">{selected.size} selected</Badge>
          <div className="flex items-center gap-1.5 ml-2">
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5"
              onClick={handleBulkSendLinks} disabled={bulkSending} data-testid="bulk-send-links">
              {bulkSending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Send Interview Links
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5"
              onClick={handleBulkExportCSV} data-testid="bulk-export-csv">
              <Download className="h-3 w-3" /> Export CSV
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5"
              onClick={handleOpenBulkTag} data-testid="bulk-tag">
              <Tag className="h-3 w-3" /> Tag
            </Button>
            {selected.size === 1 && (
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5"
                onClick={handleOpenEdit} data-testid="bulk-edit">
                <Edit2 className="h-3 w-3" /> Edit
              </Button>
            )}
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto"
            onClick={clearSelection} data-testid="bulk-clear">
            <X className="h-3 w-3 mr-1" /> Clear
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <ScrollArea className="w-full">
          <table className="w-full text-sm" data-testid="candidates-table">
            <thead>
              <tr className="border-b bg-muted/30">
                {showBulkActions && (
                  <th className="px-3 py-2.5 w-[40px]">
                    <Checkbox
                      checked={paged.length > 0 && paged.every(c => selected.has(c.id))}
                      onCheckedChange={() => toggleSelectAll(filtered, page, pageSize)}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                )}
                <th className="text-left px-3 py-2.5 w-[250px]">
                  <SortHeader label="Candidate" field="name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                </th>
                {showJobColumn && (
                  <th className="text-left px-3 py-2.5">
                    <SortHeader label="Job" field="job_title" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </th>
                )}
                <th className="text-left px-3 py-2.5 w-[100px]">
                  <SortHeader label="Added" field="created_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                </th>
                <th className="text-center px-3 py-2.5 w-[80px]">
                  <SortHeader label="Score" field="final_score" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-center" />
                </th>
                <th className="text-center px-3 py-2.5 w-[100px]">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fit</span>
                </th>
                <th className="text-center px-3 py-2.5 w-[130px]">
                  <SortHeader label="Interview" field="interview_score" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-center" />
                </th>
                <th className="text-center px-3 py-2.5 w-[60px]">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">CV</span>
                </th>
                {onOpenInterview && (
                  <th className="text-center px-3 py-2.5 w-[60px]">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Action</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr><td colSpan={(showJobColumn ? 7 : 6) + (showBulkActions ? 1 : 0) + (onOpenInterview ? 1 : 0)} className="text-center py-12 text-muted-foreground">No candidates match your filters</td></tr>
              ) : paged.map((c) => {
                const scoreColor = c.final_score >= 80 ? 'text-emerald-500' : c.final_score >= 60 ? 'text-blue-500' : c.final_score >= 40 ? 'text-amber-500' : 'text-red-500';
                const ivCfg = INTERVIEW_STATUS_CONFIG[c.interview_status] || INTERVIEW_STATUS_CONFIG.none;
                const IvIcon = ivCfg.icon;

                return (
                  <tr key={`${c.job_id}-${c.id}`} className={`border-b last:border-0 hover:bg-muted/20 transition-colors group ${selected.has(c.id) ? 'bg-primary/5' : ''}`}
                    data-testid={`table-row-${c.id}`}>
                    {/* Checkbox */}
                    {showBulkActions && (
                      <td className="px-3 py-2.5">
                        <Checkbox
                          checked={selected.has(c.id)}
                          onCheckedChange={() => toggleSelect(c.id)}
                          data-testid={`select-row-${c.id}`}
                        />
                      </td>
                    )}
                    {/* Name */}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-indigo-500">{(c.name || '?')[0].toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <button className="text-sm font-medium truncate block text-left hover:text-primary transition-colors"
                              onClick={() => setInsightCandidate(c)} data-testid={`name-click-${c.id}`}>
                              {c.name}
                            </button>
                            <span className="shrink-0"><ScoutBadge candidate={c} readOnly={readOnly} /></span>
                          </div>
                          <p className="text-[11px] text-muted-foreground truncate">{c.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Job */}
                    {showJobColumn && (
                      <td className="px-3 py-2.5">
                        <button className="text-xs text-muted-foreground hover:text-primary truncate block max-w-[180px] text-left transition-colors"
                          onClick={() => navigate(`/jobs/${c.job_id}`)} data-testid={`job-click-${c.id}`}>
                          {c.job_title}
                        </button>
                      </td>
                    )}

                    {/* Date Added */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs text-muted-foreground">{formatDate(c.created_at)}</span>
                    </td>

                    {/* Score */}
                    <td className="px-3 py-2.5 text-center">
                      {c.final_score != null ? (
                        <button className={`text-sm font-bold ${scoreColor} hover:underline`}
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}
                          onClick={() => setFitmentCandidate(c)} data-testid={`score-click-${c.id}`}>
                          {Math.round(c.final_score)}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>

                    {/* Fit Label */}
                    <td className="px-3 py-2.5 text-center">
                      {c.fit_label ? (
                        <Badge className={`text-[10px] ${FIT_COLORS[c.fit_label] || 'bg-muted'}`}>{c.fit_label}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>

                    {/* Interview Status */}
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <Badge className={`text-[10px] gap-0.5 ${ivCfg.color}`}>
                          <IvIcon className="h-3 w-3" />
                          {ivCfg.label}
                        </Badge>
                        {c.interview_status === 'completed' && c.attempts?.filter(a => a.status === 'completed').length > 1 ? (
                          <div className="flex items-center gap-1 flex-wrap justify-center">
                            {c.attempts.filter(a => a.status === 'completed').map((att, ai) => (
                              <button key={ai}
                                className={`text-[10px] font-bold hover:underline px-1 rounded ${att.score === c.interview_score ? 'text-emerald-500 bg-emerald-500/10' : 'text-muted-foreground'}`}
                                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                                onClick={() => window.open(`${PUBLIC_URL}/interview/${att.token}?recruiter=1`, '_blank')}
                                data-testid={`iv-attempt-${c.id}-${ai}`}
                                title={`Attempt ${att.attempt || ai + 1}: Score ${att.score ? Math.round(att.score) : '—'}`}>
                                {att.score ? Math.round(att.score) : '—'}
                              </button>
                            ))}
                          </div>
                        ) : c.interview_status === 'completed' && c.interview_score != null ? (
                          <button className="text-xs font-bold text-emerald-500 hover:underline"
                            style={{ fontFamily: 'JetBrains Mono, monospace' }}
                            onClick={() => window.open(`${PUBLIC_URL}/interview/${c.interview_token}?recruiter=1`, '_blank')}
                            data-testid={`iv-score-click-${c.id}`}>
                            {Math.round(c.interview_score)}
                          </button>
                        ) : null}
                        {c.interview_count > 1 && (
                          <span className="text-[9px] text-muted-foreground">{c.interview_count} attempts</span>
                        )}
                      </div>
                    </td>

                    {/* Resume */}
                    <td className="px-3 py-2.5 text-center">
                      {c.has_resume && (
                        <button className="text-muted-foreground hover:text-primary transition-colors"
                          onClick={() => onViewResume ? onViewResume(c) : navigate(`/jobs/${c.job_id}`)}
                          data-testid={`resume-view-${c.id}`}>
                          <Eye className="h-4 w-4 mx-auto" />
                        </button>
                      )}
                    </td>

                    {/* Actions: Interview Link + Email */}
                    {onOpenInterview && (
                      <td className="px-3 py-2.5 text-center">
                        <button
                          className="text-primary hover:bg-primary/10 p-1 rounded transition-colors"
                          onClick={() => onOpenInterview(c)}
                          data-testid={`interview-action-${c.id}`}
                          title="Generate Interview Link / Email"
                        >
                          <Video className="h-4 w-4 mx-auto" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollArea>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page === 0}
              onClick={() => setPage(p => p - 1)} data-testid="table-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = totalPages <= 5 ? i : Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
              return (
                <Button key={pageNum} variant={page === pageNum ? 'default' : 'outline'} size="icon"
                  className="h-8 w-8 text-xs" onClick={() => setPage(pageNum)}>
                  {pageNum + 1}
                </Button>
              );
            })}
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)} data-testid="table-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Popups */}
      <FitmentPopup candidate={fitmentCandidate} open={!!fitmentCandidate} onClose={() => setFitmentCandidate(null)} />
      <CandidateInsightPopup candidate={insightCandidate} open={!!insightCandidate} onClose={() => setInsightCandidate(null)} />

      {/* Bulk Tag Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent className="sm:max-w-[400px]" data-testid="bulk-tag-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Tag className="h-5 w-5 text-primary" /> Tag {selected.size} Candidate{selected.size > 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={selectedTagId} onValueChange={setSelectedTagId}>
              <SelectTrigger data-testid="bulk-tag-select"><SelectValue placeholder="Select a tag..." /></SelectTrigger>
              <SelectContent>
                {availableTags.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.name}
                    </div>
                  </SelectItem>
                ))}
                {availableTags.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">No tags created yet. Go to Settings to create tags.</div>
                )}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowTagDialog(false)}>Cancel</Button>
              <Button size="sm" onClick={handleBulkTag} disabled={!selectedTagId || bulkTagging} data-testid="bulk-tag-apply">
                {bulkTagging ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Tag className="h-3.5 w-3.5 mr-1" />}
                Apply Tag
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Candidate Dialog */}
      <Dialog open={!!editCandidate} onOpenChange={v => !v && setEditCandidate(null)}>
        <DialogContent className="sm:max-w-[400px]" data-testid="edit-candidate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Edit2 className="h-5 w-5 text-primary" /> Edit Candidate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))}
                data-testid="edit-name" className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))}
                type="email" data-testid="edit-email" className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Phone</Label>
              <Input value={editForm.phone} onChange={e => setEditForm(f => ({...f, phone: e.target.value}))}
                data-testid="edit-phone" className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Location</Label>
              <Input value={editForm.location} onChange={e => setEditForm(f => ({...f, location: e.target.value}))}
                data-testid="edit-location" className="h-9" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setEditCandidate(null)}>Cancel</Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={editSaving} data-testid="edit-save">
                {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SmartCandidateTable;
